import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from 'lodash'
import { Icon } from '@iconify/react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Avatar, Button, Modal, Popover, Table, Tooltip } from 'antd'
import ProjectMultiSelect from '../../../components/ProjectMultiSelect'

import {
    clientsState,
    fetchClients,
    fetchProjectsForClientModal,
    resetClientModalProjects,
    setClientModalProjectSearch,
    updateClient,
} from '../clients.slice'
import { showActionNotification } from '../../../components/notification'
import { getRandomAvatar } from '../../../assets/clients/avatar'
import clientsListStyles from './ClientsList.module.css'
import ClientsEmptyState from './ClientsEmptyState'

import styles from './ClientsListTableView.module.css'

const getInitials = (client) => {
    if (client?.firstName && client?.lastName) {
        return (
            client.firstName.charAt(0).toUpperCase() +
            client.lastName.charAt(0).toUpperCase()
        )
    }
    const fullName = client?.userName || client?.name || ''
    if (!fullName) return ''
    const nameParts = fullName.trim().split(/\s+/)
    if (nameParts.length >= 2) {
        return (
            nameParts[0].charAt(0).toUpperCase() +
            nameParts[nameParts.length - 1].charAt(0).toUpperCase()
        )
    }
    return nameParts[0]?.charAt(0).toUpperCase() || ''
}

const ClientsListTableView = ({
    onEditClient,
    apiSearch = '',
    includeDeleted,
    projectFilterIds = [],
    canEdit = true,
    canDelete = true,
}) => {
    const dispatch = useDispatch()
    const { clients, status, isNext, clientPage } = useSelector(clientsState)
    const {
        clientModalProjectsListData = [],
        clientModalProjectPage,
        clientModalProjectSearch,
        clientModalIsNext,
        clientModalListStatus,
    } = useSelector(clientsState)
    const [openPopovers, setOpenPopovers] = useState({})
    const [selectedProjectsToAdd, setSelectedProjectsToAdd] = useState({})
    const [updatingClients, setUpdatingClients] = useState({})
    const autoFillPagesRef = useRef(0)

    useEffect(() => {
        autoFillPagesRef.current = 0
    }, [apiSearch, includeDeleted, projectFilterIds])

    const clientFilter = useCallback(
        () =>
            projectFilterIds.length > 0 ? { projectIds: projectFilterIds } : {},
        [projectFilterIds]
    )

    const handleArchiveClient = useCallback(
        (client) => {
            if (!canDelete) return
            const clientId = client?._id
            if (!clientId) return
            const isArchived = Boolean(client?.isDeleted)

            Modal.confirm({
                title: isArchived ? 'Unarchive client?' : 'Archive client?',
                content: isArchived
                    ? 'This will restore the client back to the active list.'
                    : 'This will archive the client and hide them from the list.',
                okText: isArchived ? 'Unarchive' : 'Archive',
                okButtonProps: isArchived ? {} : { danger: true },
                cancelText: 'Cancel',
                onOk: async () => {
                    setUpdatingClients((prev) => ({
                        ...prev,
                        [clientId]: true,
                    }))
                    try {
                        const result = await dispatch(
                            updateClient({
                                id: clientId,
                                data: { isDeleted: !isArchived },
                            })
                        )
                        if (result?.payload?.data?.success) {
                            showActionNotification({
                                type: 'success',
                                message: isArchived
                                    ? 'Client unarchived'
                                    : 'Client archived',
                                subMessage: client?.userName || client?.email,
                                notificationKey: isArchived
                                    ? 'client-unarchived'
                                    : 'client-archived',
                            })
                            dispatch(
                                fetchClients({
                                    skip: 0,
                                    limit: 10,
                                    search: apiSearch,
                                    filter: clientFilter(),
                                    includeDeleted,
                                })
                            )
                        }
                    } catch {
                        showActionNotification({
                            type: 'error',
                            message: isArchived
                                ? 'Could not unarchive client'
                                : 'Could not archive client',
                            subMessage: 'Please try again.',
                            notificationKey: isArchived
                                ? 'client-unarchive-failed'
                                : 'client-archive-failed',
                        })
                    } finally {
                        setUpdatingClients((prev) => ({
                            ...prev,
                            [clientId]: false,
                        }))
                    }
                },
            })
        },
        [dispatch, apiSearch, includeDeleted, clientFilter, canDelete]
    )

    const handlePopoverOpenChange = (clientId, open) => {
        if (open && !canEdit) return
        setOpenPopovers((prev) => ({ ...prev, [clientId]: open }))
        if (open) {
            dispatch(resetClientModalProjects())
            dispatch(
                fetchProjectsForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: '',
                })
            )
        } else {
            setSelectedProjectsToAdd((prev) => {
                const next = { ...prev }
                delete next[clientId]
                return next
            })
        }
    }

    const handleProjectSearch = debounce((value) => {
        dispatch(setClientModalProjectSearch(value))
        dispatch(
            fetchProjectsForClientModal({
                skip: 0,
                filter: value ? { search: value } : {},
                limit: 10,
                search: value,
            })
        )
    }, 300)

    const handleProjectScroll = (e) => {
        const { target } = e
        if (
            target.scrollHeight - target.scrollTop <=
                target.clientHeight + 50 &&
            clientModalIsNext &&
            clientModalListStatus !== 'loading'
        ) {
            const nextPage = clientModalProjectPage + 1
            const skip = (nextPage - 1) * 10
            dispatch(
                fetchProjectsForClientModal({
                    skip,
                    filter: clientModalProjectSearch
                        ? { search: clientModalProjectSearch }
                        : {},
                    limit: 10,
                    search: clientModalProjectSearch,
                })
            )
        }
    }

    const getAvailableProjects = (client) => {
        const assignedProjectIds = new Set(
            (client?.projects || []).map((p) => p?._id)
        )
        return clientModalProjectsListData.filter(
            (project) => !assignedProjectIds.has(project._id)
        )
    }

    const handleRemoveProject = async (clientId, projectId, clientName) => {
        setUpdatingClients((prev) => ({ ...prev, [clientId]: true }))
        try {
            const client = clients.find((c) => c?._id === clientId)
            if (!client) return
            const updatedProjectIds = client?.projects
                .filter((p) => p?._id === projectId)
                .map((p) => p?._id)
            const result = await dispatch(
                updateClient({
                    id: clientId,
                    data: {
                        removeProjectIds: updatedProjectIds,
                    },
                })
            )
            if (result?.payload?.data?.success) {
                showActionNotification({
                    message: 'Project removed successfully',
                    subMessage: clientName,
                    notificationKey: 'client-created',
                })
                await dispatch(
                    fetchClients({
                        skip: 0,
                        limit: 10,
                        search: apiSearch,
                        filter: clientFilter(),
                        includeDeleted,
                    })
                )
            }
        } finally {
            setUpdatingClients((prev) => ({ ...prev, [clientId]: false }))
        }
    }

    const handleAddProjects = async (clientId) => {
        const projectsToAdd = selectedProjectsToAdd[clientId] || []
        if (projectsToAdd.length === 0) return
        setUpdatingClients((prev) => ({ ...prev, [clientId]: true }))
        try {
            const client = clients.find((c) => c._id === clientId)
            if (!client) return
            const existingProjectIds = client?.projects?.map((p) => p?._id)
            const newProjectIds = [...existingProjectIds, ...projectsToAdd]
            const result = await dispatch(
                updateClient({
                    id: clientId,
                    data: { addProjectIds: newProjectIds },
                })
            )
            if (result?.payload?.data?.success) {
                setSelectedProjectsToAdd((prev) => {
                    const next = { ...prev }
                    delete next[clientId]
                    return next
                })
                await dispatch(
                    fetchClients({
                        skip: 0,
                        limit: 10,
                        search: apiSearch,
                        filter: clientFilter(),
                        includeDeleted,
                    })
                )
            }
        } finally {
            setUpdatingClients((prev) => ({ ...prev, [clientId]: false }))
        }
    }

    const renderProjectManagementContent = (client) => {
        const isLoading = updatingClients[client?._id]
        const availableProjects = getAvailableProjects(client)
        const projectCount = client?.projects?.length || 0
        return (
            <div
                style={{
                    width: '340px',
                    maxHeight: 'min(calc(60vh - 50px), 330px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: '0 16px 16px',
                }}
            >
                <div
                    style={{
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflowY: 'auto',
                        padding: '2px 4px 12px 2px',
                    }}
                    className={clientsListStyles.popoverScrollArea}
                >
                    <div className={clientsListStyles.popoverProjectsHeader}>
                        <div className={clientsListStyles.popoverProjectsTitle}>
                            Current Projects
                            <span
                                className={
                                    clientsListStyles.popoverProjectsCount
                                }
                            >
                                {projectCount}
                            </span>
                        </div>
                    </div>
                    {projectCount > 0 ? (
                        <div className={clientsListStyles.popoverProjectsList}>
                            {(client?.projects || []).map((project) => (
                                <div
                                    key={project._id}
                                    className={
                                        clientsListStyles.popoverProjectChip
                                    }
                                >
                                    <Icon
                                        icon="mdi:folder-outline"
                                        className={
                                            clientsListStyles.popoverProjectChipIcon
                                        }
                                        color={project?.colorCode || '#6366f1'}
                                    />
                                    <span
                                        className={
                                            clientsListStyles.popoverProjectChipText
                                        }
                                    >
                                        {project?.name || 'N/A'}
                                    </span>
                                    <button
                                        type="button"
                                        className={
                                            clientsListStyles.popoverProjectRemoveBtn
                                        }
                                        onClick={() =>
                                            handleRemoveProject(
                                                client?._id,
                                                project?._id,
                                                client?.userName
                                            )
                                        }
                                        disabled={isLoading}
                                    >
                                        <Icon icon="mdi:close" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={clientsListStyles.popoverEmptyState}>
                            No projects assigned
                        </div>
                    )}
                </div>
                <div
                    style={{
                        flexShrink: 0,
                        borderTop: '1px solid #e2e8f0',
                        paddingTop: '10px',
                    }}
                >
                    <div
                        style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#64748b',
                            marginBottom: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Add Projects
                    </div>
                    <div className="project-popover-select">
                        <ProjectMultiSelect
                            variant="form"
                            placeholder="Search and select projects..."
                            projects={availableProjects}
                            showSearch
                            filterOption={false}
                            onSearch={handleProjectSearch}
                            onPopupScroll={handleProjectScroll}
                            loading={clientModalListStatus === 'loading'}
                            page={clientModalProjectPage}
                            value={selectedProjectsToAdd[client?._id] || []}
                            onChange={(values) =>
                                setSelectedProjectsToAdd((prev) => ({
                                    ...prev,
                                    [client?._id]: values,
                                }))
                            }
                            style={{ width: '100%', marginBottom: '12px' }}
                        />
                    </div>
                    <Button
                        type="primary"
                        size="large"
                        block
                        onClick={() => handleAddProjects(client?._id)}
                        disabled={
                            isLoading ||
                            !selectedProjectsToAdd[client?._id] ||
                            selectedProjectsToAdd[client?._id]?.length === 0
                        }
                        loading={isLoading}
                        className={clientsListStyles.addProjectsButton}
                    >
                        <Icon
                            icon="mdi:plus-circle"
                            className={clientsListStyles.addProjectsButtonIcon}
                        />
                        Add Projects
                    </Button>
                </div>
            </div>
        )
    }

    const fetchMoreClients = useCallback(() => {
        if (!isNext || status === 'loading') return
        const nextPage = clientPage + 1
        const skip = (nextPage - 1) * 10
        dispatch(
            fetchClients({
                skip,
                limit: 10,
                search: apiSearch,
                filter: clientFilter(),
                includeDeleted,
            })
        )
    }, [
        dispatch,
        isNext,
        status,
        clientPage,
        apiSearch,
        includeDeleted,
        clientFilter,
    ])

    useLayoutEffect(() => {
        if (!isNext || status !== 'loaded') return
        if (autoFillPagesRef.current >= 30) return

        const el = document.getElementById('clients-scroll-container')
        if (!el) return

        const tolerance = 4
        const noOverflow = el.scrollHeight <= el.clientHeight + tolerance

        if (noOverflow) {
            autoFillPagesRef.current += 1
            fetchMoreClients()
        }
    }, [clients.length, isNext, status, fetchMoreClients])

    const columns = [
        {
            title: 'Client',
            key: 'client',
            width: 280,
            render: (_, client) => {
                const avatarSrc =
                    client?.profilePicture ||
                    getRandomAvatar({
                        gender: client?.gender,
                        seed: client?._id || client?.email || client?.userName,
                    })
                return (
                    <div className={styles.clientCell}>
                        <Avatar size={36} src={avatarSrc}>
                            {!avatarSrc ? getInitials(client) : null}
                        </Avatar>
                        <div className={styles.clientInfo}>
                            <div className={styles.clientName}>
                                {client?.userName || 'N/A'}
                            </div>
                            <div className={styles.clientEmail}>
                                {client?.email || 'N/A'}
                            </div>
                        </div>
                    </div>
                )
            },
        },
        {
            title: 'Projects',
            key: 'projects',
            width: 500,
            render: (_, client) => {
                const projects = client?.projects || []
                const visible = projects.slice(0, 2)
                const remaining = Math.max(0, projects.length - visible.length)
                return (
                    <div className={styles.projectsCell}>
                        {visible.map((project) => (
                            <Tooltip key={project?._id} title={project?.name}>
                                <span className={styles.projectPill}>
                                    <Icon
                                        icon="mdi:folder-outline"
                                        width={14}
                                        height={14}
                                        style={{
                                            color:
                                                project?.colorCode || '#6366f1',
                                            minWidth: '14px',
                                        }}
                                    />
                                    <span className={styles.projectPillText}>
                                        {project?.name || 'N/A'}
                                    </span>
                                </span>
                            </Tooltip>
                        ))}
                        {remaining > 0 && (
                            <span className={styles.moreProjectsPill}>
                                +{remaining}
                            </span>
                        )}
                        {canEdit ? (
                            <Popover
                                content={renderProjectManagementContent(client)}
                                arrow={false}
                                title={
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            color: 'var(--textColorDarkSlate)',
                                        }}
                                    >
                                        <Icon
                                            icon="mdi:folder-multiple-outline"
                                            style={{
                                                fontSize: '16px',
                                                color: '#4f46e5',
                                            }}
                                        />
                                        Manage Projects
                                    </div>
                                }
                                trigger="click"
                                open={openPopovers[client?._id]}
                                onOpenChange={(open) =>
                                    handlePopoverOpenChange(client?._id, open)
                                }
                                placement="bottomLeft"
                                classNames={{
                                    root: 'clients-projects-popover',
                                }}
                                styles={{
                                    root: {
                                        borderRadius: '12px',
                                    },
                                    body: {
                                        padding: 0,
                                        overflow: 'hidden',
                                        borderRadius: '12px',
                                    },
                                }}
                            >
                                <Tooltip title="Click to manage projects">
                                    <button
                                        type="button"
                                        className={styles.projectsManageBtn}
                                        aria-label="Manage projects"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Icon
                                            icon="mdi:folders-outline"
                                            className={styles.projectsIcon}
                                        />
                                        <span className={styles.projectsBadge}>
                                            {projects.length}
                                        </span>
                                    </button>
                                </Tooltip>
                            </Popover>
                        ) : (
                            <span className={styles.projectsBadge}>
                                {projects.length}
                            </span>
                        )}
                    </div>
                )
            },
        },
        {
            title: 'Status',
            key: 'status',
            width: 130,
            render: (_, client) =>
                client?.isDeleted ? (
                    <span className={styles.statusArchived}>Archived</span>
                ) : (
                    <span className={styles.statusActive}>Active</span>
                ),
        },
        ...(canEdit || canDelete
            ? [
                  {
                      title: 'Actions',
                      key: 'actions',
                      width: 130,
                      render: (_, client) => (
                          <div className={styles.actionButtons}>
                              {canEdit ? (
                                  <Tooltip title="Edit Client">
                                      <button
                                          type="button"
                                          className={styles.iconBtn}
                                          onClick={() =>
                                              onEditClient?.(client)
                                          }
                                      >
                                          <Icon
                                              icon="mynaui:edit"
                                              width={16}
                                              height={16}
                                          />
                                      </button>
                                  </Tooltip>
                              ) : null}
                              {canDelete ? (
                                  <Tooltip
                                      title={
                                          client?.isDeleted
                                              ? 'Unarchive Client'
                                              : 'Archive Client'
                                      }
                                  >
                                      <button
                                          type="button"
                                          className={styles.iconBtn}
                                          onClick={() =>
                                              handleArchiveClient(client)
                                          }
                                      >
                                          <Icon
                                              icon={
                                                  client?.isDeleted
                                                      ? 'mdi:archive-arrow-up-outline'
                                                      : 'mdi:archive-outline'
                                              }
                                              width={16}
                                              height={16}
                                          />
                                      </button>
                                  </Tooltip>
                              ) : null}
                          </div>
                      ),
                  },
              ]
            : []),
    ]

    if (status === 'loading' && clients.length === 0) {
        return (
            <div className={styles.loadingWrap}>
                <Icon
                    icon="line-md:loading-loop"
                    style={{ fontSize: 48, color: '#6366f1' }}
                />
            </div>
        )
    }

    if (!clients.length) {
        return <ClientsEmptyState search={apiSearch} />
    }

    return (
        <div className={styles.tableWrap}>
            <InfiniteScroll
                dataLength={clients.length}
                next={fetchMoreClients}
                hasMore={isNext}
                initialLoad={false}
                scrollThreshold="200px"
                loader={
                    status === 'loading' ? (
                        <div className={styles.scrollLoader}>
                            <Icon
                                icon="line-md:loading-loop"
                                style={{ fontSize: 26, color: '#6366f1' }}
                            />
                        </div>
                    ) : null
                }
                scrollableTarget="clients-scroll-container"
            >
                <div className={styles.tableCard}>
                    <Table
                        className={styles.table}
                        rowKey="_id"
                        columns={columns}
                        dataSource={clients}
                        pagination={false}
                    />
                </div>
            </InfiniteScroll>
        </div>
    )
}

export default ClientsListTableView
